// antiCutEngine — pure, testable logic for the "anti-cortes" player.
//
// The AntiCutPlayer component runs a 1-second watchdog that samples the
// <video> element and asks this module whether the stream is healthy or
// needs a restart. Two failure modes are covered:
//
//   1. startupTimeout — the player was created but never produced a first
//      frame (the legacy "se queda cargando" failure the old player couldn't
//      recover from, because mpegts.js never fires ERROR in that case).
//   2. stall — playback was running but currentTime stopped advancing while
//      the video is neither paused nor ended.
//
// Restarts use an exponential backoff with a hard cap and unlimited
// attempts. The attempt counter resets after a period of healthy playback.

/** Milliseconds of frozen currentTime (while not paused) before restarting. */
export const STALL_THRESHOLD_MS = 5000;
/** Milliseconds without a first frame after player creation before restarting. */
export const STARTUP_TIMEOUT_MS = 15000;
/** First restart delay. Also the minimum — backend needs ~1s to free the Redis slot. */
export const BASE_RESTART_DELAY_MS = 1000;
/** Backoff cap between restart attempts. */
export const MAX_RESTART_DELAY_MS = 15000;
/** Continuous healthy playback needed to reset the attempt counter. */
export const HEALTHY_RESET_MS = 30000;

/**
 * Delay before restart attempt N (1-based): 1s, 2s, 4s, 8s, capped at 15s.
 *
 * @param {number} attempt 1-based attempt number.
 * @returns {number} delay in milliseconds.
 */
export function computeBackoffDelay(attempt) {
  const n = Math.max(1, Math.floor(attempt) || 1);
  return Math.min(BASE_RESTART_DELAY_MS * Math.pow(2, n - 1), MAX_RESTART_DELAY_MS);
}

/**
 * Next attempt number after a failure: resets to 1 when the stream played
 * healthily for HEALTHY_RESET_MS before failing, otherwise increments.
 *
 * @param {number} currentAttempt   attempts used so far (0 if none).
 * @param {number} healthyPlaybackMs milliseconds of healthy playback before this failure.
 * @returns {number} the attempt number to use for the upcoming restart.
 */
export function nextAttempt(currentAttempt, healthyPlaybackMs) {
  if (healthyPlaybackMs >= HEALTHY_RESET_MS) return 1;
  return (currentAttempt || 0) + 1;
}

/**
 * Create watchdog state for a freshly created player instance.
 *
 * @param {number} now epoch ms.
 */
export function createWatchdog(now) {
  return {
    startedAt: now, // when the player instance was created
    firstFrameAt: null, // when currentTime first advanced past 0
    lastProgressAt: now, // last time currentTime advanced
    lastCurrentTime: -1
  };
}

/**
 * Evaluate one watchdog sample. Mutates `wd` and returns a verdict.
 *
 * @param {object} wd     state from createWatchdog().
 * @param {object} sample { now, currentTime, paused, ended, visible }
 * @param {object} [opts] { startupTimeoutMs, stallThresholdMs }
 * @returns {{action: 'none'|'restart', reason: string}}
 */
export function evaluateWatchdog(wd, sample, opts = {}) {
  const startupTimeoutMs = opts.startupTimeoutMs ?? STARTUP_TIMEOUT_MS;
  const stallThresholdMs = opts.stallThresholdMs ?? STALL_THRESHOLD_MS;
  const {now, currentTime, paused, ended, visible} = sample;

  if (!visible) {
    // Hidden tab: browsers throttle timers and may pause decoding. Re-anchor
    // so we never count hidden time as stall time.
    wd.lastProgressAt = now;
    if (wd.firstFrameAt === null) wd.startedAt = now;
    return {action: 'none', reason: 'hidden'};
  }

  if (paused || ended) {
    // User-paused (or ended) is not a stall.
    wd.lastProgressAt = now;
    return {action: 'none', reason: paused ? 'paused' : 'ended'};
  }

  if (currentTime > wd.lastCurrentTime) {
    wd.lastCurrentTime = currentTime;
    wd.lastProgressAt = now;
    if (wd.firstFrameAt === null && currentTime > 0) {
      wd.firstFrameAt = now;
    }
    return {action: 'none', reason: 'progress'};
  }

  // No progress in this sample.
  if (wd.firstFrameAt === null) {
    if (now - wd.startedAt >= startupTimeoutMs) {
      return {action: 'restart', reason: 'startupTimeout'};
    }
    return {action: 'none', reason: 'startingUp'};
  }

  if (now - wd.lastProgressAt >= stallThresholdMs) {
    return {action: 'restart', reason: 'stall'};
  }
  return {action: 'none', reason: 'waiting'};
}

/**
 * Append the player-health correlation id as ?_vid=<id> to a streaming URL.
 * Preserves the stable correlation contract; safe on malformed URLs.
 *
 * @param {string} url
 * @param {string|null} vid
 * @returns {string}
 */
export function appendVid(url, vid) {
  if (!vid) return url;
  try {
    const u = new URL(url, window.location.href);
    // A cached M3U URL may contain an obsolete id. The current tab owns the
    // relay session, so always replace it with its stable id.
    u.searchParams.set('_vid', vid);
    return u.toString();
  } catch (_) {
    return url;
  }
}
